import React, { useState, useRef } from 'react';
import { Upload, message, Input, Form, Row, Col, Spin, Progress } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import axios, { type AxiosProgressEvent } from 'axios';

import { getPdf2SvgApiUrl } from '../../services/pdf2svg';
import Button from '../../components/common/Button';

type MetaFields = {
  form_description: string;
  form_ref: string;
  reference_document: string;
  numbering_prefix: string;
};

const CorPdfToSvgConvert: React.FC = () => {
  const [metaFields, setMetaFields] = useState<MetaFields>({
    form_description: '',
    form_ref: '',
    reference_document: '',
    numbering_prefix: '',
  });
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  const handleMetaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMetaFields(prev => ({ ...prev, [name]: value }));
  };

  const handleUpload = (info: any) => {
    let file = info.file.originFileObj || info.file;
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfFileName(file.name);
      setSvgContent(null);
      setError(null);
      message.success('PDF dosyası seçildi.');
    } else {
      setPdfFile(null);
      setPdfFileName(null);
      setSvgContent(null);
      setError(null);
      message.error('Lütfen geçerli bir PDF dosyası seçin.');
    }
  };

  const handleConvert = async () => {
    if (!pdfFile) {
      message.error('Lütfen önce bir PDF dosyası yükleyin.');
      return;
    }
    setLoading(true);
    setUploadPercent(0);
    setSvgContent(null);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      const pdf2SvgApiUrl = getPdf2SvgApiUrl();
      // axios ile gönderim ve yükleme ilerlemesi
      const response = await axios.post(pdf2SvgApiUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt: AxiosProgressEvent) => {
          const total = evt.total ?? 0;
          if (total > 0) {
            const loaded = typeof evt.loaded === 'number' ? evt.loaded : 0;
            const percent = Math.round((loaded / total) * 100);
            setUploadPercent(percent);
          }
        },
        responseType: 'text',
      });
      const svgText = response.data as string;
      setSvgContent(svgText);
      message.success('SVG başarıyla oluşturuldu!');
    } catch (err: any) {
      setError('SVG oluşturulamadı: ' + (err.message || 'Bilinmeyen hata'));
      message.error('SVG oluşturulamadı: ' + (err.message || 'Bilinmeyen hata'));
    }
    setLoading(false);
  };

  return (
    <div>
      <h1>PDF2SVG Dönüştürücü</h1>
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Form Description" required>
              <Input name="form_description" value={metaFields.form_description} onChange={handleMetaChange} placeholder="Form açıklaması girin" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Form Ref" required>
              <Input name="form_ref" value={metaFields.form_ref} onChange={handleMetaChange} placeholder="Form referansı girin" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Reference Document">
              <Input name="reference_document" value={metaFields.reference_document} onChange={handleMetaChange} placeholder="Referans doküman bilgisi" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Numbering Prefix for Filled Forms">
              <Input name="numbering_prefix" value={metaFields.numbering_prefix} onChange={handleMetaChange} placeholder="Örn: WP-" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <Upload
          accept="application/pdf"
          showUploadList={false}
          customRequest={({ file, onSuccess }) => { onSuccess && onSuccess('ok'); }}
          onChange={handleUpload}
        >
          <Button icon={<UploadOutlined />} size="middle" style={{ minWidth: 140 }}>PDF Yükle</Button>
        </Upload>
        {pdfFileName && <span style={{ marginLeft: 10 }}>{pdfFileName}</span>}
      </div>
      <Button
        type="primary"
        onClick={handleConvert}
        disabled={!pdfFile || loading}
        loading={loading}
        style={{ marginBottom: 16 }}
      >
        PDF'yi SVG'ye Dönüştür
      </Button>
      {loading && (
        <div style={{ maxWidth: 600, marginBottom: 12 }}>
          <Progress percent={Math.min(uploadPercent, 100)} status="active" />
        </div>
      )}
      {loading && <Spin tip="Dönüştürülüyor..." />}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div style={{marginTop:24}}>
        {svgContent && (
          <div
            ref={svgContainerRef}
            style={{
              border: '1px solid #ccc',
              display: 'inline-block',
              background: '#fff',
              maxWidth: '100%',
              overflow: 'auto',
            }}
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        )}
      </div>
    </div>
  );
};

export default CorPdfToSvgConvert; 